export const SHELL_DRAWER_MEDIA_QUERY = "(max-width: 58rem)";

// Navigation density is an operator preference, not workspace data. Keep one
// versioned key for both the application shell and the reusable navigation
// component so an account refresh can never restore a competing rail state.
export const SHELL_NAVIGATION_COLLAPSED_KEY = "crm_sidebar_collapsed.v2";

const text = (value) => String(value ?? "").trim();

/**
 * Preserve an operator's desktop navigation preference when the viewport
 * temporarily becomes a mobile drawer. The drawer must begin closed, but a
 * responsive breakpoint must never silently overwrite the preference that is
 * restored on a wider canvas.
 */
export function reconcileNavigationForViewport({
  isMobile,
  currentCollapsed,
  desktopCollapsed,
}) {
  if (typeof isMobile !== "boolean") {
    throw new TypeError("isMobile must be a boolean");
  }
  if (typeof currentCollapsed !== "boolean") {
    throw new TypeError("currentCollapsed must be a boolean");
  }
  if (typeof desktopCollapsed !== "boolean") {
    throw new TypeError("desktopCollapsed must be a boolean");
  }

  if (isMobile) {
    return {
      collapsed: true,
      desktopCollapsed: currentCollapsed,
    };
  }

  return {
    collapsed: desktopCollapsed,
    desktopCollapsed,
  };
}

/**
 * The desktop rail and the mobile drawer intentionally share the same
 * preference, but not the same interaction layer. A backdrop and an inert
 * work canvas exist only while the mobile drawer is actually open. Keeping
 * this decision here prevents a visually hidden desktop backdrop from
 * remaining in the DOM during an account or route transition.
 */
export function resolveNavigationPresentation({ isMobile, collapsed }) {
  if (typeof isMobile !== "boolean") {
    throw new TypeError("isMobile must be a boolean");
  }
  if (typeof collapsed !== "boolean") {
    throw new TypeError("collapsed must be a boolean");
  }

  const drawerOpen = isMobile && !collapsed;
  return {
    drawerOpen,
    showBackdrop: drawerOpen,
    inertMainContent: drawerOpen,
  };
}

export function resolveWorkspaceDisplayName(user) {
  return text(user?.tenantDisplayName) || "Current account";
}

/**
 * All authenticated entry points lead through Account Setup. It is the
 * account-scoped control plane that establishes workspace context before an
 * operator opens a dashboard or an operational module. API authorization
 * remains authoritative for every subsequent route and request.
 */
export function resolveShellHomePath() {
  return "/AccountSetup";
}
